import React, { useEffect, useState } from "react";
import { Button, Tooltip, message } from "antd";
import { useRecoilState } from "recoil";
import { breadcrumbState } from "../../../lib/stores/breadcrumb";
import { post } from "../../../services/apiService";
import getApiEndpoints from "../../../services/endpoints";
import { companyState } from "../../../lib/stores/companyId";
import CommonTable from "../../../lib/components/Table/table";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { CopyOutlined } from "@ant-design/icons";

interface DataType {
  title: string;
  width: number;
  dataIndex: string;
  key: string;
  sorter: boolean;
}

// Function to check if a string is an ISO 8601 date
const isISO8601Date = (value: any) => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

const copyTextToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).then(
    () => {
      message.success("Text copied to clipboard!");
    },
    (err) => {
      console.error("Failed to copy text: ", err);
    }
  );
};

const formatNumber = (number) => {
  const formattedNumber = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(number);

  return formattedNumber;
};

function padZero(num) {
  return num < 10 ? "0" + num : num;
}

const renderStatus = (value?: any, key?: any, record?: any) => {
  if (key === "total" || key === "total") {
    const total = formatNumber(value); // Adjust decimal places as needed
    return `${total}`;
  }
  if (key === "revenue") {
    //const total = formatNumber((record.total / 1.12).toFixed(2)); // Adjust decimal places as needed
     const total = formatNumber((record.revenue).toFixed(2));
    return `${total}`;
  }
  if (key === "gst") {
    //const total = (record.total / 1.12).toFixed(2);
      const total = (record.total-record.gst).toFixed(2);
    const finalGST = Number(record.total) - Number(total);
    const GST = formatNumber(finalGST.toFixed(2)); // Adjust decimal places as needed
    return `${GST}`;
  }
  if (key === "cycle" && value === "90") {
    return "PAYMENT FAILED!";
  }
  if (key === "cycle" && value === "91") {
    return "PAYMENT TIMEOUT";
  }
  if (key === "amount") {
    const amount = formatNumber(value); // Adjust decimal places as needed
    return amount ? `${amount}` : "NA";
  }
  // if (key === "revenue") {
  //   const amount = formatNumber((record.amount / 1.12).toFixed(2)); // Adjust decimal places as needed
  //   return `${amount}`;
  // }
  // if (key === "gst") {
  //   const amount = (record.amount / 1.12).toFixed(2);
  //   const finalGST = Number(record.amount) - Number(amount);
  //   const GST = formatNumber(finalGST.toFixed(2)); // Adjust decimal places as needed
  //   return `${GST}`;
  // }
  if (key === "average") {
    // Calculate the average if both glassesCount and orderCount are available
    if (
      record.glassesCount != null &&
      record.orderCount != null &&
      record.orderCount !== 0
    ) {
      const average = (record.glassesCount / record.orderCount).toFixed(2); // Adjust decimal places as needed
      return `${average}`;
    } else {
      return "NA";
    }
  }

  if (key === "date") {
    if (value !== null && isISO8601Date(value)) {
      const date = new Date(value);
      return `${date.getFullYear()}-${padZero(date.getMonth() + 1)}-${padZero(
        date.getDate()
      )} ${padZero(date.getHours())}:${padZero(date.getMinutes())}:${padZero(
        date.getSeconds()
      )}`;
    }
  }

  // Handling other truthy values with ellipsis and tooltip
  if (value) {
    return (
      <Tooltip title={value}>
        <div className="ellipsis-cell">{value}</div>
      </Tooltip>
    );
  }

  // Default case for falsy values other than boolean false
  return value === 0 ? 0 : "NA";
};

const columns: ColumnsType<DataType> = [
  {
    title: "Order Date",
    width: 120,
    dataIndex: "orderDate",
    key: "orderDate",
    align: "center",
    sorter: (a: any, b: any) =>
      (a?.orderDate?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.orderDate?.toString() ?? "").toLowerCase()),
    render: renderStatus,
  },
  {
    title: "Location",
    dataIndex: "location",
    key: "location",
    align: "center",
    width: 220,
    sorter: (a: any, b: any) =>
      (a?.storeId?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.storeId?.toString() ?? "").toLowerCase()),
    render: renderStatus,
  },
  {
    title: "Transaction Id",
    dataIndex: "transactionId",
    key: "transactionId",
    align: "center",
    width: 200,
    sorter: (a: any, b: any) =>
      (a?.transactionId?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.transactionId?.toString() ?? "").toLowerCase()),
    render: renderStatus,
  },
  {
    title: "Glass Count",
    width: 100,
    dataIndex: "glassCount",
    key: "glassCount",
    align: "center",
    sorter: (a: any, b: any) => {
      const countA = a?.glassCount ?? 0; // Fallback to 0 if glassCount is null or undefined
      const countB = b?.glassCount ?? 0; // Fallback to 0 if glassCount is null or undefined
      return countA - countB;
    },
    render: (value) => renderStatus(value, "glassCount"),
  },
  {
    title: "Revenue",
    dataIndex: "revenue",
    key: "revenue",
    align: "right",
    width: 100,
    sorter: false,
    render: (value, record) => renderStatus(value, "revenue", record),
  },
  {
    title: "GST",
    dataIndex: "gst",
    key: "gst",
    align: "right",
    width: 100,
    sorter: false,
    render: (value, record) => renderStatus(value, "gst", record),
  },
  {
    title: "Total",
    width: 100,
    dataIndex: "total",
    key: "total",
    align: "center",
    sorter: (a: any, b: any) => {
      const countA = a?.total ?? 0; // Fallback to 0 if total is null or undefined
      const countB = b?.total ?? 0; // Fallback to 0 if total is null or undefined
      return countA - countB;
    },
    render: (value, record) => renderStatus(value, "total", record),
  },
  {
    title: "Start Time",
    dataIndex: "startTime",
    key: "startTime",
    width: 150,
    align: "center",
    sorter: (a: any, b: any) =>
      (a?.startTime?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.startTime?.toString() ?? "").toLowerCase()),
    render: (value) => renderStatus(value, "date"),
  },
  {
    title: "Payment Time",
    dataIndex: "paymentTime",
    width: 150,
    key: "paymentTime",
    align: "center",
    sorter: (a: any, b: any) =>
      (a?.paymentTime?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.paymentTime?.toString() ?? "").toLowerCase()),
    render: (value) => renderStatus(value, "date"),
  },
  {
    title: "Completed Time",
    dataIndex: "completedTime",
    width: 150,
    key: "completedTime",
    align: "center",
    sorter: (a: any, b: any) =>
      (a?.completedTime?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.completedTime?.toString() ?? "").toLowerCase()),
    render: (value) => renderStatus(value, "date"),
  },
  {
    title: "Failed Reason",
    width: 200,
    dataIndex: "failedReason",
    key: "failedReason",
    sorter: true,
    align: "center",
    render: renderStatus,
  },
  {
    title: "Failed Code",
    dataIndex: "failedCode",
    width: 200,
    key: "cycleFailedCode",
    align: "center",
    sorter: true,
    render: (value) => renderStatus(value, "cycle"),
  },
  {
    title: "Machine Name",
    dataIndex: "machineName",
    key: "machineName",
    width: 150,
    align: "center",
    sorter: (a: any, b: any) =>
      (a?.machineName?.toString() ?? "")
        .toLowerCase()
        .localeCompare((b?.machineName?.toString() ?? "").toLowerCase()),
    render: renderStatus,
  },
];

// Activate the customParseFormat plugin to handle specific date formats
dayjs.extend(customParseFormat);
// Format today's date in the desired format
const today = dayjs().format("DD/MM/YYYY");
const loadInitialValues = () => {
  const storedDates = sessionStorage.getItem("selectedDates");
  let formattedDates = [null, null];

  if (storedDates) {
    const dates = JSON.parse(storedDates);
    // Explicitly parse each date with the expected format
    formattedDates = [
      dayjs(dates[0], "DD/MM/YYYY").isValid()
        ? dayjs(dates[0], "DD/MM/YYYY")
        : null,
      dayjs(dates[1], "DD/MM/YYYY").isValid()
        ? dayjs(dates[1], "DD/MM/YYYY")
        : null,
    ];
  } else {
    formattedDates = [dayjs(today, "DD/MM/YYYY"), dayjs(today, "DD/MM/YYYY")];
  }

  const initialValues = {
    Machine: "All",
    Location: "All",
    dates: formattedDates,
  };

  return initialValues;
};

const OrderFailure: React.FC = () => {
  const initialValues = loadInitialValues();
  const [, setBreadCrumb] = useRecoilState(breadcrumbState);
  const [machineDropdown, setMachines] = useState<any>([]);
  const [locationDropdown, setLocations] = useState<any>([]);
  const [companyId] = useRecoilState(companyState);
  const [rowData, setRowData] = useState<any>();
  const user: any =
    localStorage.getItem("user") || sessionStorage.getItem("user");
  const userData: any = JSON.parse(user);
  const userRole = userData?.roles[0];
  const apiEndpoints = getApiEndpoints(userRole);

  useEffect(() => {
    setBreadCrumb([
      { label: "Transactions", href: "/order-failure" },
      { label: "Order Failure", href: "/order-failure" },
    ]);
  }, [setBreadCrumb]);

  useEffect(() => {
    const payLoad = {
      companyId: localStorage.getItem("companyId") || companyId,
    };
    const fetchDropdowns = async () => {
      const responseMachine = await post(
        apiEndpoints.getAllMachinesByCompanyId,
        payLoad
      );
      if (
        responseMachine &&
        responseMachine?.status === 200 &&
        responseMachine?.data
      ) {
        setMachines([
          { machineName: "All", machineId: "all" },
          ...responseMachine.data,
        ]);
      }
      const responseLocation = await post(
        apiEndpoints.getAllLocationsByCompany,
        payLoad
      );
      if (
        responseLocation &&
        responseLocation?.status === 200 &&
        responseLocation?.data
      ) {
        setLocations([
          { locationName: "All", locationId: "All" },
          ...responseLocation.data,
        ]);
      }
    };
    fetchDropdowns();
  }, []);

  const onSubmit = async (values: any) => {
    try {
      const dates = values?.dates?.map(
        (date: { format: (arg0: string) => any }) => date.format("DD/MM/YYYY")
      );
      const payLoad = {
        fromDate: dates[0],
        toDate: dates[1],
        companyId: localStorage.getItem("companyId") || companyId,
        machineId: values?.Machine === "All" ? null : values?.Machine,
        locationId: values?.Location === "All" ? null : values?.Location,
      };
      const response = await post(
        apiEndpoints.getOrderFailuresByCompanyId,
        payLoad
      );
      if (response.status === 200) {
        setRowData(response.data);
      }
    } catch (error) {
      console.log(error);
      message.error("Error while fetching");
    }
  };

  const calculateTotals = (data: any) => {
    return data?.reduce(
      (acc, record) => {
        // Update totals for each column
        acc.orderCount += record?.orderCount;
        acc.glassesCount += record?.glassesCount;
        acc.amount += record?.amount;
        /*acc.revenue += parseFloat((record?.amount / 1.12).toFixed(2)); // Example for revenue, adjust accordingly
        acc.gst += parseFloat(
          (Number(record?.amount) - record?.amount / 1.12).toFixed(2)
        );
        acc.aurobindoRevenue += parseFloat(
          ((record?.amount / 1.12) * 0.1).toFixed(2)
        );*/
          acc.revenue +=record?.revenue;
          acc.gst +=record?.gst;
          acc.aurobindoRevenue += parseFloat(
              ((record?.revenue)* 0.1).toFixed(2)
          );
        return acc;
      },
      {
        orderCount: 0,
        glassesCount: 0,
        amount: 0,
        revenue: 0,
        gst: 0,
        aurobindoRevenue: 0,
      } // Initial totals
    );
  };

  const totals = calculateTotals(rowData);

  return (
    <>
      <CommonTable
        routePath={""}
        showDates={true}
        showDropDowns={true}
        pageName="Order-Failure"
        dataSource={rowData}
        columns={columns}
        initialValues={initialValues}
        // machines={machineDropdown}
        locations={locationDropdown}
        onFinish={onSubmit}
        // footer={() => (
        //   <div style={{ display: "flex", justifyContent: "space-between" }}>
        //     <b>Total Amount: {formatNumber(totals?.amount)}</b>
        //     <b>Total Order Count: {totals?.orderCount}</b>
        //     <b>Total Glasses Count: {totals?.glassesCount}</b>
        //     <b>Total Revenue: {formatNumber(totals?.revenue.toFixed(2))}</b>
        //     <b>Total GST: {formatNumber(totals?.gst.toFixed(2))}</b>
        //     {/* <b>Aurobindo Revenue: {totals?.aurobindoRevenue.toFixed(2)}</b> */}
        //   </div>
        // )}
      />
    </>
  );
};

export default OrderFailure;
